import { PhysicsMotionType, Vector3 } from '@babylonjs/core';
import { Builder, IBuilder } from 'builder-pattern';
import { CrumblingPlatformBehavior, CrumblingPlatformConfig } from './behavior/CrumblingPlatformBehavior';
import { MovingPlatformBehavior, MovingPlatformConfig } from './behavior/MovingPlatformBehavior';
import { Entity } from './Entity';
import { Level } from './Level';
import { MeshConfig, MeshFactory } from './MeshFactory';

export interface Platform extends Entity {}

export class PlatformConfig {
  static builder(): IBuilder<PlatformConfig> {
    return Builder(PlatformConfig, {});
  }

  /**
   * Mesh config of the platform
   */
  mesh: MeshConfig;

  /**
   * Position of the platform (static platform)
   */
  position?: Vector3;

  /**
   * Moving platform config (moving platform)
   */
  moving?: MovingPlatformConfig;

  /**
   * Crumbling platform config (crumbling platform)
   */
  crumbling?: CrumblingPlatformConfig;
}

export class PlatformFactory {
  public static create(config: PlatformConfig, level: Level, pathIndex: number): Platform {
    // Add behavior to mesh config
    if (!config.mesh.behaviors) {
      config.mesh.behaviors = [];
    }

    let movingBehavior: MovingPlatformBehavior | undefined;
    if (config.moving && config.moving.start && config.moving.end && config.moving.speed) {
      // Add MovingPlatformBehavior if this is a moving platform
      config.mesh.physicsMotionType = PhysicsMotionType.ANIMATED;
      config.mesh.disablePreStep = false; // Allow mesh to control physics body
      movingBehavior = new MovingPlatformBehavior(
        config.moving.start!.clone(),
        config.moving.end!.clone(),
        config.moving.speed!,
        config.moving.direction || 1
      );
      config.mesh.behaviors.push(movingBehavior);
    }
    if (config.crumbling) {
      // Attach crumbling behavior
      const crumblingBehavior = new CrumblingPlatformBehavior(config.crumbling);
      config.mesh.behaviors.push(crumblingBehavior);
    }

    config.mesh.metadata = {
      projectileCollision: true,
      platform: true,
    };

    const mesh = config.mesh.buildMesh(level);
    if (config.position) {
      mesh.position = config.position.clone();
    }

    MeshFactory.configureMesh(mesh, config.mesh, level);

    mesh.checkCollisions = true;

    const platform: Platform = {
      mesh,
      config,
      pathIndex,
    };

    return platform;
  }
}
